import { FormControl, FormGroup } from '@angular/forms';
import { IDateRangePickerDate } from '../../../shared/components/date-range-picker/date-range-picker.interface';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { QuickItemGeneratorComponent } from '../../../shared/components/quick-item-generator/quick-item-generator.component';
import { IBoard, IBoardTeamAssignmentBase, IBoardUserAssignment } from '../../settings/boards/boards.interface';
import { TTeam } from '../../settings/users-teams/teams/teams.interface';
import {
  ICustomDateRangeFieldValue,
  ICustomOptionFieldValue,
  ICustomTextFieldValue,
  IItemBase,
  IItemJoins,
  IItemRecurrence,
} from '../items.interface';
import { IRecurringItemFormGroup } from './recurring-item-configuration/recurring-item-configuration.interface';

export type TFormControlItem = {
  assignee: FormControl<IDropdownOption[] | null>;
  boardId: FormControl<IDropdownOption[] | null>;
  cronEditor: FormGroup<IRecurringItemFormGroup>;
  description: FormControl<string | null>;
  dueDate: FormControl<IDateRangePickerDate | null>;
  itemCategory: FormControl<IDropdownOption[] | null>;
  itemType: FormControl<IDropdownOption<string>[] | null>;
  name: FormControl<string | null>;
  reporter: FormControl<IDropdownOption[] | null>;
  status: FormControl<IDropdownOption[] | null>;
};

type TItemBoardItemConfiguration = Pick<IItemJoins, 'boardItemConfiguration'>;
export type TAddItem = Pick<
  IItemBase,
  | 'name'
  | 'description'
  | 'boardId'
  | 'boardItemConfigurationId'
  | 'assigneeId'
  | 'currentWorkflowStepId'
  | 'dueDate'
  | 'values'
> &
  TItemBoardItemConfiguration['boardItemConfiguration']['itemType'] & {
    itemRecurrence?: Omit<IItemRecurrence, 'id'>;
  };
export type TEditItem = Partial<
  Pick<
    IItemBase,
    'name' | 'description' | 'assigneeId' | 'currentWorkflowStepId' | 'boardItemConfigurationId' | 'dueDate' | 'values'
  >
>;

export interface IEditItem {
  dto: TEditItem;
  id: number;
}

export type TCustomFieldFormKey = `${string}_${string}`;

export type TCustomFieldFormValue =
  | ICustomTextFieldValue['selectedValue']
  | ICustomDateRangeFieldValue['selectedValue']
  | ICustomOptionFieldValue['selectedValue'];

export type TFormItem = {
  assignee: IDropdownOption[];
  boardId: IDropdownOption[];
  customFields: Record<TCustomFieldFormKey, TCustomFieldFormValue>;
  description: string;
  itemCategory: IDropdownOption[];
  itemType: IDropdownOption<string>[];
  name: string;
  status: IDropdownOption[];
};

export type TItemModalComponentData = {
  idForEdit?: number;
  quickItem?: QuickItemGeneratorComponent['formGroup']['value'] & {
    itemType: IDropdownOption<string>[] | null;
  };
  selectedBoard?: IDropdownOption | null;
  submitButtonText: string;
  title?: string;
};

export type TBoardJoins = IBoard & {
  boardTeamAssignments: (IBoardTeamAssignmentBase & { team: TTeam })[];
  boardUserAssignments: IBoardUserAssignment[];
};

export interface IBoardMembersRequest {
  boardId: number;
  search?: string;
  selectedUserId?: number | null;
  escalatedBoardId?: number | null;
}

export interface IGetItemWorkInstructionAssignment {
  boardId: number;
  itemId: number;
}

export const itemFieldsThatCanBeRecursivelyUpdated = [
  'assigneeId',
  'description',
  'boardItemConfigurationId',
  'name',
  'values',
] as const;

export type TItemFieldThatCanBeRecursivelyUpdated = (typeof itemFieldsThatCanBeRecursivelyUpdated)[number];

export const itemFieldsThatCannotBeRecursivelyUpdated = ['dueDate'] as const;

export type TItemFieldThatCannotBeRecursivelyUpdated = (typeof itemFieldsThatCannotBeRecursivelyUpdated)[number];
export type TItemUpdateFields = TItemFieldThatCanBeRecursivelyUpdated | TItemFieldThatCannotBeRecursivelyUpdated;
